<template>
    <div>
        <v-dialog :persistent="persistent" :value="value" max-width="700px" @keydown.esc.prevent="cancel">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ $t('create_location') }}</span>
                </v-card-title>

                <v-card-text>
                    <v-form ref="form" v-model="valid">
                        <v-container>
                            <!-- bezeichnung -->
                            <v-row dense>
                                <v-col cols="12">
                                    <LabelField :label="$t('bezeichnung')">
                                        <v-text-field
                                            v-model="title"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            :counter="60"
                                            :rules="[getRule('required'), getRule('maxLength')(60)]"
                                        />
                                    </LabelField>
                                </v-col>
                            </v-row>
                            <!-- street -->
                            <v-row dense>
                                <v-col cols="10">
                                    <LabelField :label="$t('strasse')">
                                        <v-text-field
                                            :value="newAddress.street"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            :rules="[getRule('required'), getRule('maxLength')(100)]"
                                        />
                                    </LabelField>
                                </v-col>
                                <v-col cols="2">
                                    <LabelField :label="$t('strasse_nr')">
                                        <v-text-field
                                            :value="newAddress.street_nr"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            :rules="[getRule('maxLength')(15)]"
                                        />
                                    </LabelField>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <!-- plz/zip, ort -->
                                <ZipCityGroup :record="newAddress" />
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <slot name="actions">
                        <v-spacer></v-spacer>
                        <v-btn text @click="cancel">{{ $t('cancel') }}</v-btn>
                        <v-btn :disabled="!valid || loading" color="action" text @click="confirm">{{
                            $t('create')
                        }}</v-btn>
                    </slot>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <ConfirmDialog
            v-if="showConfirmDialog"
            :value="showConfirmDialog"
            :title="$t('create_location')"
            :message="confirmMsg"
            :confirm-label="$t('edit_data')"
            @input="showConfirmDialog = $event"
            @cancel="cancel"
        />
    </div>
</template>

<script>
import getRulesMixin from '@/mixins/get-form-rules';
import { Location, LocationCollection } from '@/models/Location';
import { Address } from '@/models/Address';
import { userIsStaff } from '@/helpers/auth';
import { mapActions } from 'vuex';
import { userInfo, globalParam } from '@/helpers/globals';
import ZipCityGroup from './ZipCityGroup.vue';

export default {
    name: 'AddressNewRecordDialog',
    components: {
        ZipCityGroup,
    },
    mixins: [getRulesMixin],

    props: {
        persistent: {
            type: Boolean,
            default: true,
        },
        value: {
            type: Boolean,
            required: true,
        },
        addressType: {
            type: String,
            default: 'location',
        },
    },

    data() {
        const locationCollection = new LocationCollection();
        if (!userIsStaff() && this.showAllLocations) {
            locationCollection.addExtraParam('showAllLocations', true);
        }

        return {
            collection: locationCollection,
            valid: null,
            title: null,
            newAddress: new Address({ type: this.addressType }),
            showConfirmDialog: false,
            loadingCount: 0,
        };
    },

    computed: {
        loading() {
            return this.loadingCount > 0;
        },
        confirmMsg() {
            const customer_support_email = globalParam('customer_support_email', null);
            const msgContactPart = customer_support_email
                ? this.$t('address_already_exists_customer', { customer_support_email })
                : this.$t('address_already_exists');
            let msg = [msgContactPart, '', this.$t('existing_addresses')];
            this.collection.each((addr) => {
                msg.push(`- ${addr.titleWithLocation}`);
            });
            return msg.join('<br>');
        },
    },

    mounted() {
        this.validateForm();
    },

    methods: {
        ...mapActions(['runAsync']),

        validateForm() {
            this.$refs.form.validate();
        },

        cancel() {
            this.$emit('cancel');
            this.$emit('input', false);
        },

        async confirm() {
            this.loadingCount++;
            try {
                this.setDuplicationCheckFilter();
                await this.checkForDuplication();
                if (this.collection.length > 0) {
                    this.showConfirmDialog = true;
                } else {
                    await this.saveNewAddress();
                }
            } finally {
                this.loadingCount--;
            }
        },

        setDuplicationCheckFilter() {
            this.collection.setFilter('title', this.title);
            this.collection.setFilter('zip', this.newAddress?.zip);
            this.collection.setFilter('city', this.newAddress?.city);
        },

        async checkForDuplication() {
            await this.runAsync(async () => this.collection.query());
        },

        async saveNewAddress() {
            this.loadingCount++;
            try {
                const type = 'location';
                const legal_type = 'organization';
                this.newAddress.set({
                    type,
                    legal_type,
                    title: this.title,
                });
                const location = new Location({
                    event_organizer_id: userIsStaff() ? null : userInfo().event_organizer_id,
                    legal_type,
                });

                await this.runAsync(() => this.newAddress.save());
                location.address_id = this.newAddress.getId();
                await this.runAsync(() => location.save());

                this.$emit('input', false);
                this.$emit('new-address', this.newAddress, location);
            } finally {
                this.loadingCount--;
            }
        },
    },
};
</script>
